import React, { useEffect, useRef } from 'react';
import { MainLayout } from '../../layouts';
import {
  HeroSection,
  ProductSection,
  UseCasesSection,
  FAQSection,
  GettingStartedSection,
  FooterSection,
} from '../../sections';

export const LandingPage: React.FC = () => {
  // Section refs for navigation
  const productRef = useRef<HTMLDivElement>(null);
  const useCasesRef = useRef<HTMLDivElement>(null);
  const gettingStartedRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Simple navigation for natural scroll layout
    (window as any).scrollToSection = (sectionId: string) => {
      const sectionMap = {
        product: productRef.current,
        'use-cases': useCasesRef.current,
        'getting-started': gettingStartedRef.current,
        faq: faqRef.current,
      };

      const targetElement = sectionMap[sectionId as keyof typeof sectionMap];

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        console.warn(`Unknown section: ${sectionId}`);
      }
    };

    return () => {
      delete (window as any).scrollToSection;
    };
  }, []);

  return (
    <MainLayout>
      {/* Hero Section - Natural document flow, takes 100vh */}
      <div className="h-screen">
        <HeroSection />
      </div>

      {/* Glass Card Container - Immediately follows hero in natural flow */}
      <div
        className="relative z-20 rounded-t-[48px] max-[599px]:rounded-t-none"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          borderBottom: 'none',
          boxShadow: `
            inset 0 2px 4px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 0 rgba(255, 255, 255, 0.3),
            0 -2px 8px 0 rgba(0, 0, 0, 0.04),
            0 -8px 16px -4px rgba(0, 0, 0, 0.08),
            0 -16px 32px -8px rgba(0, 0, 0, 0.06),
            0 -20px 40px -12px rgba(59, 130, 246, 0.05)
          `,
        }}
      >
        {/* Glassmorphism top transition overlay */}
        <div
          className="absolute inset-x-0 top-0 h-32 rounded-t-[48px] max-[599px]:h-40 max-[599px]:rounded-t-none"
          style={{
            background: `
              linear-gradient(
                to bottom,
                rgba(255, 255, 255, 0.1) 0%,
                rgba(255, 255, 255, 0.05) 50%,
                rgba(255, 255, 255, 0) 100%
              )
            `,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            pointerEvents: 'none',
          }}
        />

        {/* Section Content - Natural scroll flow */}
        <div
          className="flex min-h-screen flex-col max-[599px]:min-h-0"
          ref={productRef}
        >
          <ProductSection />
        </div>
        <div
          className="flex min-h-screen flex-col max-[599px]:min-h-0"
          ref={useCasesRef}
        >
          <UseCasesSection />
        </div>
        <div
          className="flex min-h-screen flex-col max-[599px]:min-h-0"
          ref={gettingStartedRef}
        >
          <GettingStartedSection />
        </div>
        <div className="flex min-h-screen flex-col" ref={faqRef}>
          <FAQSection />
        </div>
        <div className="flex flex-col">
          <FooterSection />
        </div>
      </div>
    </MainLayout>
  );
};
